import { DocumentNode, gql } from '@apollo/client';

import { EntityType } from 'enums';
import {
  IndicatorChart,
  FilterType,
  ChartTypeEnum,
  UserBrandEntity,
  UserSchoolEntity,
  FilterTypeEnum,
} from 'generated';

interface GenerateIndicatorQueryOptions {
  indicator?: IndicatorChart;
  entityType: EntityType;
  hasPagination?: boolean;
  firstColumId?: string;
  entity?: UserBrandEntity | UserSchoolEntity;
  similarSchoolsCompare?: boolean;
  currentFilterChartType: FilterTypeEnum;
}

type ReturnTypeKey = keyof typeof queryReturnType;

const queryReturnType = {
  likert: `{
    scaleValue
  }`,
  distribution: `{
    distribution
  }`,
  likertDistribution: `{
    scaleValue
    distribution
  }`,
  likertSimilarSchools: `{
    scaleValue
    similarScaleValue
  }`,
  distributionSimilarSchools: `{
    distribution
    similarDistribution
  }`,
  likertDistributionSimilarSchools: `{
    scaleValue
    similarScaleValue
    distribution
    similarDistribution
  }`,
};

const scatterBySchoolQuery = gql`
  query getScatterIndicator(
    $indicatorEventId: String!
    $area: AreaEnum!
    $segment: SegmentEnum!
    $id: String!
  ) {
    data: schoolEffectResultBySchool(
      indicatorEventId: $indicatorEventId
      area: $area
      segment: $segment
      id: $id
    ) {
      expectedProficiencyLine
      regularPoints
      highlightPoints {
        label
        points
      }
    }
  }
`;

const scatterByCompanyQuery = gql`
  query getScatterIndicator(
    $indicatorEventId: String!
    $area: AreaEnum!
    $segment: SegmentEnum!
  ) {
    data: schoolEffectResultByCompany(
      indicatorEventId: $indicatorEventId
      area: $area
      segment: $segment
    ) {
      expectedProficiencyLine
      regularPoints
      highlightPoints {
        label
        points
      }
    }
  }
`;

const scatterByBrandQuery = gql`
  query getScatterIndicator(
    $indicatorEventId: String!
    $area: AreaEnum!
    $segment: SegmentEnum!
    $id: String!
  ) {
    data: schoolEffectResultByBrand(
      indicatorEventId: $indicatorEventId
      area: $area
      segment: $segment
      id: $id
    ) {
      expectedProficiencyLine
      regularPoints
      highlightPoints {
        label
        points
      }
    }
  }
`;

const scatterQueries = {
  [EntityType.BRAND]: scatterByBrandQuery,
  [EntityType.COMPANY]: scatterByCompanyQuery,
  [EntityType.SCHOOL]: scatterBySchoolQuery,
};

export function indicatorQueryBuilder({
  indicator,
  entityType,
  similarSchoolsCompare,
  firstColumId = 'entityName',
  hasPagination = true,
  currentFilterChartType = FilterTypeEnum.General,
}: GenerateIndicatorQueryOptions): DocumentNode {
  const currentChartType =
    indicator?.chartType[currentFilterChartType] || 'likert';
  const isNSE = indicator?.scale === 8;
  const columns = (indicator?.filters?.columns ||
    []) as unknown as FilterType[];
  const chartType =
    indicator?.chartType[
      currentFilterChartType as keyof typeof indicator.chartType
    ] || 'likert';

  if (chartType === ChartTypeEnum.Scatter) {
    return scatterQueries[entityType];
  }

  let pagination = '';

  const queryType: ReturnTypeKey = (
    !isNSE &&
    entityType === EntityType.SCHOOL &&
    similarSchoolsCompare &&
    currentChartType !== ChartTypeEnum.Scatter
      ? `${chartType}SimilarSchools`
      : chartType || (chartType as any)?.GENERAL || 'likert'
  ) as ReturnTypeKey;

  if (hasPagination) {
    pagination = `

      pagination: _pagination {
        hasNextPage
        totalPage
        currentPage
        totalCount
      }
    `;
  }

  const data = `

      data {
        ${firstColumId}
        ${columns.map(({ id }) => `${id} ${queryReturnType[queryType]}\n`)}
      }
  `;

  const query = `
  query getIndicatorData(
    $indicatorEventId: String!
    $indicatorId: String!
    $entityId: String!
    $filterType: FilterTypeEnum!
    $page: Int!
    $limit: Int!
    $entityNameFilter: String
  ) {
    data: indicatorResult(
      indicatorEventId: $indicatorEventId
      indicatorId: $indicatorId
      entityId: $entityId
      filterType: $filterType
      limit: $limit
      page: $page
      entityNameFilter: $entityNameFilter
    ) {
      ${pagination}

      ${data}
    }
  }
  `.replaceAll('\n', '');

  return gql(query);
}
